import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useLogout } from '../session/auth-actions';
import { useCurrentUser } from '../session/current-user';
import { roleLabel } from '../session/roles';
import { ChevronDownIcon, SignOutIcon } from '../components/icons';

/**
 * Account menu: a disclosure button that reveals who is signed in — email, role (human label:
 * Admin / ICT) and Bekebi User ID, all from the API session — and the sign-out action (the real
 * POST /api/auth/logout flow), with a link to change one's own password. Escape or a click outside closes it; closing with Escape returns
 * focus to the button.
 */
export function AccountMenu() {
  const user = useCurrentUser();
  const { logout, isPending } = useLogout();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const role = roleLabel(user.role);

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={`Account: ${user.email}`}
        onClick={() => setOpen((value) => !value)}
        className="flex min-h-10 max-w-full items-center gap-2.5 rounded-full py-1 pl-1 pr-2.5 text-sm font-medium text-fg-2 hover:bg-raised hover:text-fg"
      >
        <span
          aria-hidden="true"
          className="flex size-8 shrink-0 items-center justify-center rounded-full bg-info-soft text-sm font-semibold uppercase text-accent-link"
        >
          {user.email.charAt(0)}
        </span>
        <span className="hidden max-w-48 truncate md:inline">{user.email}</span>
        <ChevronDownIcon className="size-4 shrink-0 text-muted" />
      </button>

      {open ? (
        <div
          id={panelId}
          role="region"
          aria-label="Account"
          className="absolute right-0 z-30 mt-2 w-72 max-w-[calc(100vw-2rem)] rounded-[var(--radius-card)] border border-line bg-surface p-4 shadow-2xl shadow-black/40"
        >
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted">Email</dt>
              <dd className="mt-0.5 break-all text-fg">{user.email}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted">Role</dt>
              <dd className="mt-0.5 text-fg">{role}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted">
                Bekebi User ID
              </dt>
              <dd className="mt-0.5 font-mono text-fg">{user.bekebiUserId}</dd>
            </div>
          </dl>
          <Link
            to="/app/account/password"
            onClick={() => setOpen(false)}
            className="mt-4 flex min-h-10 w-full items-center justify-center rounded-lg border border-line-strong bg-surface px-4 text-sm font-semibold text-fg hover:border-muted hover:bg-raised"
          >
            Change password
          </Link>
          <button
            type="button"
            onClick={() => void logout()}
            disabled={isPending}
            className="mt-2 flex min-h-10 w-full items-center justify-center gap-2 rounded-lg border border-line-strong bg-surface px-4 text-sm font-semibold text-fg hover:border-muted hover:bg-raised disabled:text-faint"
          >
            <SignOutIcon className="size-4" />
            {isPending ? 'Signing out…' : 'Sign out'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
